import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdEdit, 
  MdDelete, 
  MdError, 
  MdFilterList, 
  MdCalendarToday, 
  MdScale, 
  MdCategory,
  MdLocalShipping,
  MdInventory,
  MdLocationOn,
  MdReceipt,
  MdClose,
  MdSave,
  MdVisibility
} from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { tokenManager } from "../../../utils/tokenManager";

const regions = [
  'Brong Ahafo Central',
  'Brong Ahafo North',
  'Brong Ahafo South'
];

const provinceCities = [
  'Techiman',
  'Nkoranza',
  'Kintampo',
  'Wenchi',
  'Nsawkaw',
  'Banda',
  'Nkrankwanta',
  'Danyame',
  'Berekum',
  'Dormaa',
  'Drobo',
  'Sampa',
  'Nalerigu',
  'Yamatwa',
  'Kofibadukrom',
  'Kwameseikrom',
  'Kwameprakrom'
];

const countries = [
  'Ghana',
  'Brazil'
];

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ViewDetailsModal = ({ isOpen, onClose, record }) => {
  if (!isOpen || !record) return null;
  const getQualityBadge = (label) => {
    const v = (label || '').toLowerCase();
    if (v.includes('very good')) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (v.includes('good')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    if (v.includes('regular')) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    if (v.includes('bad') && !v.includes('very')) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
    if (v.includes('very bad')) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const formatDate = (d) => {
    try {
      return d ? new Date(d).toLocaleDateString() : '-';
    } catch {
      return d || '-';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-5xl rounded-xl bg-white shadow-2xl dark:bg-navy-800">
        <div className="border-b border-gray-200 p-6 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">Record Details</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">ID: {record.id}</p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700"
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-6">
          {/* Overview */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Overview</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Detail label="Date" value={formatDate(record.date)} />
                <Detail label="Type" value={record.rcn_type} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Quality</p>
                  <div className={`mt-0.5 inline-flex items-center gap-2 px-3 py-1 rounded-md text-xs font-semibold ${getQualityBadge(record.quality_rcn)}`}>
                    <span>{record.quality_rcn || '-'}</span>
                  </div>
                </div>
                <Detail label="Status" value={record.status_approval} />
                <Detail label="Agent" value={record.agent} />
                <Detail label="Truck Number" value={record.truck_number} />
                <Detail label="Conventional Code" value={record.conventional_code} />
                <Detail label="Reanalysis" value={record.reanalysis} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Location</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Detail label="Country" value={record.country} />
                <Detail label="Region" value={record.region} />
                <Detail label="Province/City" value={record.province_or_city} />
                <Detail label="Origin" value={record.origin} />
              </div>
            </div>
          </div>

          {/* Weights & Counts */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Weights & Counts</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Detail label="Number of Trucks" value={record.number_of_trucks} />
                <Detail label="Number of Bags" value={record.number_of_bags} />
                <Detail label="Gross Weight (kg)" value={record.gross_weight} />
                <Detail label="Net Weight (kg)" value={record.net_weight} />
              </div>
            </div>
          </div>

          {/* Quality Metrics */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Quality Metrics</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Detail label="Moisture (%)" value={record.moisture} />
                <Detail label="Outturn (LBS)" value={record.outturn} />
                <Detail label="Expected Kernel Outturn" value={record.expected_kernel_outturn} />
                <Detail label="Count per kg" value={record.count_per_kg} />
                <Detail label="Dust After Drying" value={record.dust_after_drying} />
                <Detail label="Dust After Calibration" value={record.dust_after_calibration} />
                <Detail label="Total SP+IM (%)" value={record.total_sp_plus_im_percentage} />
                <Detail label="Total Defective Rate (%)" value={record.total_defective_rate_percentage} />
              </div>
            </div>
          </div>

          {/* Impurities Breakdown */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Impurities Breakdown</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Detail label="Impurities (%)" value={record.impurities} />
                <Detail label="Bored (%)" value={record.bored} />
                <Detail label="Void (%)" value={record.void} />
                <Detail label="Oil (%)" value={record.oil} />
                <Detail label="Rotten (%)" value={record.rotten} />
                <Detail label="Total Impurities (%)" value={record.total_impurities} />
              </div>
            </div>
          </div>

          {/* Size Distribution */}
          <div>
            <h4 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">Size Distribution (%)</h4>
            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <Detail label="Size 15" value={record.size_15} />
                <Detail label="Size 17" value={record.size_17} />
                <Detail label="Size 19" value={record.size_19} />
                <Detail label="Size 21" value={record.size_21} />
                <Detail label="Size 23" value={record.size_23} />
                <Detail label="Size 25" value={record.size_25} />
                <Detail label="Size 28" value={record.size_28} />
              </div>
              <div className="mt-4">
                <Detail label="Total Size (%)" value={record.total_size} />
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 p-4 dark:border-navy-700">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm font-semibold text-navy-700 dark:text-white">{value ?? '-'}</p>
  </div>
);

const RecordDetailsModal = ({ isOpen, onClose, recordData }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (recordData) {
      setIsLoading(true);
      // Simulate loading delay
      setTimeout(() => {
        setFormData(recordData);
        setIsLoading(false);
      }, 500);
    }
  }, [recordData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }
      // Ensure the date is in YYYY-MM-DD format
      const formattedDate = formData.date
        ? (typeof formData.date === 'string' ? formData.date : new Date(formData.date).toISOString().split('T')[0])
        : '';
      
      // Prepare payload to match the new API schema
      const updateData = {
        date: formattedDate,
        status_approval: formData.status_approval,
        agent: formData.agent,
        region: formData.region,
        province_or_city: formData.province_or_city,
        country: formData.country,
        rcn_type: formData.rcn_type,
        number_of_trucks: parseInt(formData.number_of_trucks),
        number_of_bags: parseInt(formData.number_of_bags),
        count_per_kg: parseFloat(formData.count_per_kg),
        moisture: parseFloat(formData.moisture),
        outturn: parseFloat(formData.outturn),
        dust_after_drying: parseFloat(formData.dust_after_drying),
        dust_after_calibration: parseFloat(formData.dust_after_calibration),
        gross_weight: parseFloat(formData.gross_weight),
        net_weight: parseFloat(formData.net_weight),
        size_15: parseFloat(formData.size_15),
        size_17: parseFloat(formData.size_17),
        size_19: parseFloat(formData.size_19),
        size_21: parseFloat(formData.size_21),
        size_23: parseFloat(formData.size_23),
        size_25: parseFloat(formData.size_25),
        size_28: parseFloat(formData.size_28),
        total_size: parseFloat(formData.total_size),
        impurities: parseFloat(formData.impurities),
        bored: parseFloat(formData.bored),
        void: parseFloat(formData.void),
        oil: parseFloat(formData.oil),
        rotten: parseFloat(formData.rotten),
        total_impurities: parseFloat(formData.total_impurities),
        immature_percentage: parseFloat(formData.immature_percentage),
        spotted_percentage: parseFloat(formData.spotted_percentage),
        total_sp_plus_im_percentage: parseFloat(formData.total_sp_plus_im_percentage),
        total_defective_rate_percentage: parseFloat(formData.total_defective_rate_percentage),
        very_bad: parseFloat(formData.very_bad),
        bad: parseFloat(formData.bad),
        regular: parseFloat(formData.regular),
        good: parseFloat(formData.good),
        very_good: parseFloat(formData.very_good),
        expected_kernel_outturn: parseFloat(formData.expected_kernel_outturn),
        origin: formData.origin,
        quality_rcn: formData.quality_rcn,
        good_measured_in_grams: parseFloat(formData.good_measured_in_grams),
        conventional_code: formData.conventional_code,
        truck_number: formData.truck_number,
        reanalysis: formData.reanalysis
      };

      console.log('Updating record id:', formData.id, 'payload:', updateData);

      const response = await axios.put(
        `https://usibras-web-backend.onrender.com/api/new-raw-rcn/${formData.id}`,
        updateData,
        { headers: { 'Authorization': authHeader } }
      );

      if (response.data && response.data.message) {
        toast.success(response.data.message);
        onClose();
        // Refresh the table data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Error updating record. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-4xl rounded-xl bg-white shadow-2xl dark:bg-navy-800">
        {/* Fixed Header */}
        <div className="border-b border-gray-200 p-6 dark:border-navy-700">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">Edit Record Details</h3>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700"
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex h-[400px] items-center justify-center">
              <div className="relative">
                <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
                <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
                <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
                <div className="mt-8 text-center">
                  <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
                  <p className="text-sm text-gray-400">Please wait...</p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Basic Information</h4>
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">ID</label>
                          <input
                            type="text"
                            name="id"
                            value={formData.id}
                            disabled
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Date</label>
                          <input
                            type="date"
                            name="date"
                            value={formData.date ? new Date(formData.date).toISOString().split('T')[0] : ''}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Type</label>
                          <input
                            type="text"
                            name="rcn_type"
                            value={formData.rcn_type || ''}
                            disabled
                            className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400 cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Quality</label>
                          <input
                            type="text"
                            name="quality_rcn"
                            value={formData.quality_rcn || ''}
                            disabled
                            className="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400 cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Location Details</h4>
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Country</label>
                          <select
                            name="country"
                            value={formData.country || ''}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          >
                            <option value="">Select country</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Region</label>
                          <select
                            name="region"
                            value={formData.region || ''}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          >
                            <option value="">Select region</option>
                            {regions.map((region) => (
                              <option key={region} value={region}>
                                {region}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Province/City</label>
                          <select
                            name="province_or_city"
                            value={formData.province_or_city || ''}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          >
                            <option value="">Select province or city</option>
                            {provinceCities.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Origin</label>
                          <input
                            type="text"
                            name="origin"
                            value={formData.origin}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Quality Metrics</h4>
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Moisture (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="moisture"
                            value={formData.moisture}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Outturn (LBS)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="outturn"
                            value={formData.outturn}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                      <div>
                        <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Dust After Drying</label>
                        <input
                          type="number"
                          step="0.01"
                          name="dust_after_drying"
                          value={formData.dust_after_drying}
                          onChange={handleInputChange}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Dust After Calibration</label>
                        <input
                          type="number"
                          step="0.01"
                          name="dust_after_calibration"
                          value={formData.dust_after_calibration}
                          onChange={handleInputChange}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                      </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Count per kg</label>
                          <input
                            type="number"
                            step="0.01"
                            name="count_per_kg"
                            value={formData.count_per_kg}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Defects & Impurities</h4>
                    <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Total Defective Rate (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="total_defective_rate_percentage"
                            value={formData.total_defective_rate_percentage}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Total Impurities (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="total_impurities"
                            value={formData.total_impurities}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Spotted Percentage (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="spotted_percentage"
                            value={formData.spotted_percentage}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Rotten (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            name="rotten"
                            value={formData.rotten}
                            onChange={handleInputChange}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">Size Distribution (%)</h4>
                <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-700">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Total Size (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        name="total_size"
                        value={formData.total_size}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 15</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_15"
                        value={formData.size_15}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 17</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_17"
                        value={formData.size_17}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 19</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_19"
                        value={formData.size_19}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 21</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_21"
                        value={formData.size_21}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 23</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_23"
                        value={formData.size_23}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 25</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_25"
                        value={formData.size_25}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">Size 28</label>
                      <input
                        type="number"
                        step="0.01"
                        name="size_28"
                        value={formData.size_28}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="border-t border-gray-200 p-6 dark:border-navy-700">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || isSubmitting}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <MdSave className="h-5 w-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, recordId, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">Confirm Deletion</h3>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this record? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReceivingRecords = () => {
  const [receivingData, setReceivingData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    origin: '',
    type: '',
    quality: ''
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    per_page: 50,
    total_pages: 0,
    total_records: 0,
  });

  // Fetch data from API
  useEffect(() => {
    const fetchReceivingData = async () => {
      try {
        setLoading(true);
        const authHeader = tokenManager.getAuthHeader();
        if (!authHeader) {
          setError('Authentication token not found. Please login again.');
          setReceivingData([]);
          setLoading(false);
          return;
        }
        const response = await axios.get(
          `https://usibras-web-backend.onrender.com/api/new-raw-rcn?page=${currentPage}`,
          { headers: { 'Authorization': authHeader } }
        );
        const payload = response.data || {};
        const dataArray = Array.isArray(payload.data) ? payload.data : [];
        setReceivingData(dataArray);
        if (payload.pagination) {
          setServerPagination({
            page: payload.pagination.page ?? currentPage,
            per_page: payload.pagination.per_page ?? 50,
            total_pages: payload.pagination.total_pages ?? 0,
            total_records: payload.pagination.total_records ?? dataArray.length,
          });
        } else {
          setServerPagination(prev => ({ ...prev, page: currentPage, total_pages: 1, total_records: dataArray.length }));
        }
        setError(null);
      } catch (err) {
        console.error('API Error:', err);
        setError('Error connecting to the server');
        setReceivingData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReceivingData();
  }, [currentPage]);

  // Calculate summary statistics
  const calculateSummary = (data) => {
    const totalReceivings = data.length;
    const numberOfTrucks = [...new Set(data.map(item => item.truck_number))].length;
    const avgMoisture = data.reduce((sum, item) => sum + (parseFloat(item.moisture) || 0), 0) / (data.length || 1);
    const avgOutturn = data.reduce((sum, item) => sum + (parseFloat(item.outturn) || 0), 0) / (data.length || 1);

    return {
      totalReceivings,
      numberOfTrucks,
      avgMoisture: avgMoisture.toFixed(2),
      avgOutturn: avgOutturn.toFixed(2)
    };
  };

  // Get unique values for filters
  const uniqueTypes = [...new Set(receivingData?.map(item => item.rcn_type) || [])];
  const uniqueOrigins = [...new Set(receivingData?.map(item => item.origin) || [])];
  const uniqueQualities = [...new Set(receivingData?.map(item => item.quality_rcn) || [])];

  // Apply filters and search
  const filteredData = receivingData.filter(item => {
    // Convert search term to lowercase for case-insensitive search
    const searchTermLower = searchTerm.toLowerCase();

    // Create an array of all searchable values from the item
    const searchableValues = [
      item.id?.toString(),
      item.date ? new Date(item.date).toLocaleDateString() : '',
      item.rcn_type,
      item.origin,
      item.quality_rcn,
      item.moisture?.toString(),
      item.outturn?.toString(),
      item.truck_number,
      item.agent,
      item.country,
      item.region,
      item.province_or_city,
      item.count_per_kg?.toString(),
      item.size_15?.toString(),
      item.size_17?.toString(),
      item.size_19?.toString(),
      item.size_21?.toString(),
      item.size_23?.toString(),
      item.size_25?.toString(),
      item.size_28?.toString(),
      item.total_size?.toString(),
      item.total_defective_rate_percentage?.toString(),
      item.total_impurities?.toString(),
      item.spotted_percentage?.toString(),
      item.rotten?.toString()
    ].filter(Boolean); // Remove null/undefined values

    // Check if any of the searchable values include the search term
    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toLowerCase().includes(searchTermLower)
      );

    // Apply date range filter
    const matchesDate = (!filters.startDate || new Date(item.date) >= new Date(filters.startDate)) &&
                       (!filters.endDate || new Date(item.date) <= new Date(filters.endDate));
    
    // Apply other filters
    const matchesType = !filters.type || item.rcn_type === filters.type;
    const matchesOrigin = !filters.origin || item.origin === filters.origin;
    const matchesQuality = !filters.quality || item.quality_rcn === filters.quality;

    return matchesSearch && matchesDate && matchesType && matchesOrigin && matchesQuality;
  });

  const summary = calculateSummary(filteredData);

  // Pagination
  const totalPages = serverPagination.total_pages || 0;
  const pageStart = receivingData.length > 0 ? (serverPagination.page - 1) * serverPagination.per_page + 1 : 0;
  const pageEnd = (serverPagination.page - 1) * serverPagination.per_page + receivingData.length;

  const handleEdit = (id) => {
    const record = receivingData.find(r => r.id === id);
    if (record) {
      setSelectedRecord(record);
      setIsModalOpen(true);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (id) => {
    setRecordToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      const response = await axios.delete(
        `https://usibras-web-backend.onrender.com/api/new-raw-rcn/${recordToDelete}`,
        { headers: { 'Authorization': authHeader } }
      );
      
      if (response.data && response.data.message) {
        toast.success(response.data.message || "Record deleted successfully");
        // Remove the deleted record from the state
        setReceivingData(receivingData.filter(item => item.id !== recordToDelete));
      } else {
        toast.success("Record deleted successfully");
        setReceivingData(receivingData.filter(item => item.id !== recordToDelete));
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const TABLE_HEAD = ["ID", "Date", "Type", "Origin", "Quality", "Moisture", "Outturn", "Dust Dry", "Dust Calib", "Order of Trucks", "Truck", "Agent", "Actions"];

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdReceipt className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Receivings</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalReceivings}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdLocalShipping className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Number of Trucks</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.numberOfTrucks}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Moisture</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.avgMoisture}%</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Outturn</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.avgOutturn} LBS</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Origin</label>
              <select
                value={filters.origin}
                onChange={(e) => setFilters({...filters, origin: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Origins</option>
                {uniqueOrigins.map(origin => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Quality</label>
              <select
                value={filters.quality}
                onChange={(e) => setFilters({...filters, quality: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Qualities</option>
                {uniqueQualities.map(quality => (
                  <option key={quality} value={quality}>{quality}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', origin: '', type: '', quality: ''})}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table header and search */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Warehouse Receiving Records
            </h4>
            <p className="mt-1 text-sm text-gray-600">
              Track all raw cashew nut receiving records
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search across all fields..."
                className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"
                    >
                      <p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {new Date(item.date).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.rcn_type}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.origin}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.quality_rcn}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.moisture}%
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.outturn} LBS
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.dust_after_drying}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.dust_after_calibration}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.number_of_trucks}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.truck_number}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.agent}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(item)}
                        className="rounded-lg bg-emerald-50 p-2 text-emerald-600 hover:bg-emerald-100 dark:bg-navy-700 dark:text-emerald-400 dark:hover:bg-navy-600"
                      >
                        <MdVisibility className="h-5 w-5" />
                      </button>
                        <button
                          onClick={() => handleEdit(item.id)}
                          className="rounded-lg bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {pageStart} to {pageEnd} of {serverPagination.total_records} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={serverPagination.page === 1 || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                serverPagination.page === 1 || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={serverPagination.page === 1 || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                serverPagination.page === 1 || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              // Calculate which page numbers to show
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (serverPagination.page <= 3) {
                pageNum = index + 1;
              } else if (serverPagination.page >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = serverPagination.page - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    serverPagination.page === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={serverPagination.page === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                serverPagination.page === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={serverPagination.page === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                serverPagination.page === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* Add the modal component */}
        <ViewDetailsModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          record={selectedRecord}
        />
        <RecordDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          recordData={selectedRecord}
        />

        {/* Add DeleteConfirmationModal */}
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDelete}
          recordId={recordToDelete}
          isDeleting={isDeleting}
        />
      </Card>
    </div>
  );
};

export default ReceivingRecords; 